/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { INVITE_SECRET } from '@/auth/inviteSecret'

// Vite `define` bakes `__DEV__` at build time; read defensively since the
// symbol is undeclared outside a Vite build.
declare const __DEV__: boolean | undefined

export type InviteStatus = 'valid' | 'invalid' | 'no-token'

// Fixed message the invite token signs. The token carries no data — it is a
// permanent proof-of-invite: the same signature is valid forever. Kept in sync
// with scripts/mint-invite.mjs, which signs this exact string.
const INVITE_PAYLOAD = 'amp-invite'

/**
 * True in dev builds, where the gate is disabled and every sign-in is allowed —
 * keeps local dev and the `--login` snap flow unaffected. A production
 * `vite build` resolves `__DEV__` to `false`, turning the gate on.
 */
export function inviteGateDisabled(): boolean {
    return __DEV__ === true
}

function base64urlToBytes(segment: string): Uint8Array<ArrayBuffer> {
    const b64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4))
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

/**
 * Verify a non-expiring, HMAC-signed invite token.
 *
 * Token shape: `base64url(HMAC_SHA256(INVITE_PAYLOAD, SECRET))`. The signature
 * is recomputed over the fixed `INVITE_PAYLOAD` string with Web Crypto and
 * compared via `crypto.subtle.verify` (constant-time by construction). There is
 * no expiry — a minted link is permanent, so it survives an iOS home-screen
 * PWA launch whenever that happens.
 *
 * In dev builds the gate is disabled, so every token — including a missing one
 * — resolves to 'valid'.
 */
export async function verifyInvite(token: string | null): Promise<InviteStatus> {
    if (inviteGateDisabled()) return 'valid'
    if (!token) return 'no-token'

    try {
        const encoder = new TextEncoder()
        const key = await crypto.subtle.importKey(
            'raw',
            encoder.encode(INVITE_SECRET),
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['verify']
        )
        const signatureOk = await crypto.subtle.verify(
            'HMAC',
            key,
            base64urlToBytes(token),
            encoder.encode(INVITE_PAYLOAD)
        )
        return signatureOk ? 'valid' : 'invalid'
    } catch {
        // Malformed base64 — treat as a bad token, not a thrown error.
        return 'invalid'
    }
}
